import { useEffect, useContext } from 'react';
import { dispatch, store } from 'redux/store';
import {
  getGetTenantsQueryKey,
  Tenant as TenantDto,
  useGetTenants,
  putFieldConfig,
} from 'api-client';
import {
  saveCurrent,
  setTenantHasOrganizations,
  clearTenantHasOrganizations,
} from 'common/infrastructure/tenant/redux';
import { AuthContext } from 'identityAndAccess/infrastructure/AwsCognitoJwtContext';
import { QueryClient } from '@tanstack/react-query';
import { queryClient } from 'common/infrastructure/remoteState/persistQueryClient';
import { externalLogout, isTokenExpirationValid } from 'identityAndAccess/infrastructure/utils';
import { AxiosError } from 'axios';
import loggerService, { LoggerLevel } from '../log';
import ReferenceTableRepository from 'assetClass/infrastructure/referenceTable/repository';
import AssetClassRepository from 'assetClass/infrastructure/assetClass/repository';
import OrganizationRepository from 'common/infrastructure/organization/repository';

const TenantRepository = {
  useTenants() {
    const { auth } = store.getState();
    // Safely get auth context - may be null if not inside AuthProvider
    const authContext = useContext(AuthContext);
    const isAuthenticated = authContext?.isAuthenticated ?? false;

    const useQueryResponse = useGetTenants({
      query: {
        enabled: isAuthenticated && !!auth.user,
      },
    });

    const data: TenantDto[] | undefined = useQueryResponse?.data;
    if (!data || data?.length === 0) {
      return {
        ...useQueryResponse,
        tenantEntities: null,
      };
    }

    const tenantEntities = data; // TODO these aren't entities, user mapper fromDto

    return {
      ...useQueryResponse,
      tenantEntities,
    };
  },

  useCurrentTenant() {
    const currentTenantPid = this.getCurrentTenantPid();
    ReferenceTableRepository.useGetReferenceTables(currentTenantPid);
    AssetClassRepository.useCurrentAssetClass(currentTenantPid);

    const { tenantEntities, ...useQueryResponse } = TenantRepository.useTenants();

    // Use useEffect to handle side effects
    useEffect(() => {
      if (tenantEntities && tenantEntities.length > 0) {
        const tenant = tenantEntities.find((t) => t.id === currentTenantPid);

        // If no currentTenantPid or no tenant is found, default to first
        if (!currentTenantPid || !tenant) {
          this.saveCurrent(tenantEntities[0]?.id);
        }
      }
    }, [tenantEntities, currentTenantPid]);

    if (!tenantEntities) {
      return { tenant: null, ...useQueryResponse };
    }

    if (useQueryResponse.failureReason) {
      const axiosResponse = useQueryResponse?.failureReason as AxiosError;
      const status = axiosResponse.response?.status;
      if (status === 404) {
        const idToken = typeof window !== 'undefined' ? localStorage.getItem('idToken') : '';
        const tokenValid = idToken ? isTokenExpirationValid(idToken) : false;
        loggerService.log(
          LoggerLevel.DEBUG,
          '[LOGOUT_TRIGGER] 404 error fetching tenants - triggering logout',
          {
            currentTenantPid: currentTenantPid,
            hasToken: !!idToken,
            tokenValid,
            errorStatus: status,
            errorData: axiosResponse.response?.data,
          }
        );
        void externalLogout({ skipLogoutWhenOffline: true });
        return {
          tenant: null,
          ...useQueryResponse,
        };
      }
    }

    if (!tenantEntities || !Array.isArray(tenantEntities)) {
      return { tenant: null, ...useQueryResponse };
    }

    const tenant = tenantEntities.find((t) => t.id === currentTenantPid);

    // If no currentTenantPid or no tenant is found, default to first
    let tenantEntity;
    if (!currentTenantPid || !tenant) {
      tenantEntity = tenantEntities[0];
    } else {
      tenantEntity = tenant;
    }

    return {
      ...useQueryResponse,
      tenant: tenantEntity,
    };
  },

  getCurrentTenantPid() {
    // This doesn't require hooks
    const { tenants: tenantStore } = store.getState();
    const { currentTenantPid }: { currentTenantPid: string } = tenantStore;

    return currentTenantPid;
  },

  /**
   * TP2 tenant id for the current tenant, read from the cached tenants query.
   *
   * Redux only holds the TP3 pid, so the TP2 id is resolved from the tenants
   * DTO instead. Returns undefined when the cache hasn't been populated yet —
   * callers are analytics-only and must degrade rather than throw.
   */
  getCurrentTenantTp2Id(): number | undefined {
    const currentTenantPid = this.getCurrentTenantPid();
    if (!currentTenantPid) {
      return undefined;
    }

    const tenants = queryClient.getQueryData<TenantDto[]>(getGetTenantsQueryKey());

    return tenants?.find((t) => t.id === currentTenantPid)?.tp2TenantId;
  },

  /**
   * Reactive counterpart to getCurrentTenantTp2Id.
   *
   * Subscribes to the tenants query so consumers re-render once the data
   * arrives. A restored session has currentTenantPid rehydrated from persisted
   * Redux while the query is still in flight, so a non-reactive read there
   * would stay undefined for the life of the session.
   *
   * Unlike useCurrentTenant this has no side effects — it neither dispatches
   * nor prefetches — so it is safe to call from providers.
   */
  useCurrentTenantTp2Id(): number | undefined {
    const currentTenantPid = this.getCurrentTenantPid();
    const { data: tenants } = useGetTenants<TenantDto[]>({
      query: { enabled: !!currentTenantPid },
    });

    if (!currentTenantPid) {
      return undefined;
    }

    return tenants?.find((t) => t.id === currentTenantPid)?.tp2TenantId;
  },

  saveCurrent(tenantId: string) {
    const currentTenantPid = this.getCurrentTenantPid();

    dispatch(saveCurrent(tenantId));

    if (currentTenantPid !== tenantId) {
      // Clear tenantHasOrganizations when switching tenants
      this.clearTenantHasOrganizations();
    }

    const { auth } = store.getState();
    if (auth.isAuthenticated) {
      this.preloadData(tenantId);
    }
  },

  async syncTenantFieldConfig(tp2TenantId: number, tp2LayerName = 'trees') {
    try {
      await putFieldConfig({
        tp2TenantId,
        tp2LayerName,
      });
    } catch (error) {
      loggerService.log(LoggerLevel.ERROR, error);
      throw error;
    }
  },

  preloadData(tenantId: string) {
    if (!tenantId) {
      return;
    }

    ReferenceTableRepository.prefetchReferenceTables(tenantId).catch((error) => {
      loggerService.log(LoggerLevel.ERROR, error);
    });

    AssetClassRepository.prefetchAssetClasses(tenantId).catch((error) => {
      loggerService.log(LoggerLevel.ERROR, error);
    });

    OrganizationRepository.prefetchOrganizations(tenantId).catch((error) => {
      loggerService.log(LoggerLevel.ERROR, error);
    });
  },

  async refetchTenants(queryClient: QueryClient) {
    await queryClient.invalidateQueries({
      queryKey: getGetTenantsQueryKey(),
    });
  },

  async refetchTenantsAndWait(queryClient: QueryClient) {
    await queryClient.refetchQueries({
      queryKey: getGetTenantsQueryKey(),
    });
  },

  getTenantHasOrganizations(): boolean | null {
    const { tenants: tenantStore } = store.getState();

    return tenantStore.tenantHasOrganizations;
  },

  useHasMultipleTenants(): boolean {
    const { tenantEntities } = this.useTenants();
    return tenantEntities ? tenantEntities.length > 1 : false;
  },

  setTenantHasOrganizations(hasOrganizations: boolean) {
    dispatch(setTenantHasOrganizations(hasOrganizations));
  },

  clearTenantHasOrganizations() {
    dispatch(clearTenantHasOrganizations());
  },
};

export { TenantRepository as default };
